import { useCallback } from 'react';

import { AsyncThunkAction } from '@reduxjs/toolkit';

import { OptionsObject, useSnackbar } from 'notistack';

function usePromiseSnackbar() {
  const { enqueueSnackbar } = useSnackbar();
  return useCallback(
    async <T>(
      promise: Promise<T> | ReturnType<AsyncThunkAction<T, unknown, unknown>>,
      successMessage?: string | ((result: T) => string),
      failureMessage?: string | ((err: any) => string),
      propagateFailure: boolean = false,
      options: OptionsObject = {},
    ): Promise<T | undefined> => {
      const finalPromise = isThunkPromise(promise) ? promise.unwrap() : promise;

      return finalPromise.then(
        (result) => {
          if (successMessage) {
            enqueueSnackbar(
              typeof successMessage === 'string' ? successMessage : successMessage(result),
              { variant: 'success', ...options },
            );
          }
          return result;
        },
        (err) => {
          if (failureMessage) {
            enqueueSnackbar(
              typeof failureMessage === 'string' ? failureMessage : failureMessage(err),
              {
                variant: 'error',
                ...options,
              },
            );
          }

          if (propagateFailure) {
            throw err;
          }
          return undefined;
        },
      );
    },
    [enqueueSnackbar],
  );
}

function isThunkPromise<T>(
  promise: Promise<T> | ReturnType<AsyncThunkAction<T, unknown, unknown>>,
): promise is ReturnType<AsyncThunkAction<T, unknown, unknown>> {
  return (
    typeof (promise as ReturnType<AsyncThunkAction<T, unknown, unknown>>).unwrap === 'function'
  );
}

export { usePromiseSnackbar };
